import { useEffect, useId, useState } from "react";
import { Eye, EyeOff, LoaderCircle, MailCheck } from "lucide-react";
import { api } from "./api.ts";
import { identifyAnalytics } from "./analytics.tsx";
import { useLocale } from "./i18n.tsx";
import { Page, type Session } from "./Shell.tsx";

type AuthMode = "login" | "register" | "forgot-password" | "reset-password" | "verify-email";

function PasswordField({ name = "password", label, autoComplete }: { name?: string; label: string; autoComplete: string }) {
  const { copy } = useLocale();
  const id = useId();
  const [visible, setVisible] = useState(false);
  return (
    <label className="form-field" htmlFor={id}>
      <span>{label}</span>
      <span className="password-input">
        <input id={id} name={name} type={visible ? "text" : "password"} minLength={8} required autoComplete={autoComplete} />
        <button type="button" onClick={() => setVisible((value) => !value)} aria-label={visible ? copy.auth.hidePassword : copy.auth.showPassword}>
          {visible ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />}
        </button>
      </span>
    </label>
  );
}

function VerifyEmail({ session }: { session: Session | null }) {
  const { copy, href, isEnglish } = useLocale();
  const [state, setState] = useState<"working" | "done" | "error">("working");
  const [message, setMessage] = useState<string>(copy.auth.verifying);
  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get("token") || "";
    if (!token) {
      setState("error");
      setMessage(copy.auth.badLink);
      return;
    }
    void api<{ user: Session }>("/api/auth/verify-email", { method: "POST", body: JSON.stringify({ token }) })
      .then(async () => {
        await identifyAnalytics();
        setState("done");
        setMessage(copy.auth.verified);
      })
      .catch((error) => {
        setState("error");
        setMessage(!isEnglish && error instanceof Error ? error.message : copy.auth.invalidLink);
      });
  }, []);
  return (
    <Page session={session} className="auth-page">
      <main id="main" className="auth-shell page-shell">
        <section className="auth-card auth-result" aria-live="polite">
          {state === "working" ? <LoaderCircle className="spinner" aria-hidden="true" /> : <MailCheck aria-hidden="true" />}
          <h1>{state === "done" ? copy.auth.readyTitle : state === "error" ? copy.auth.failedTitle : copy.auth.verifyTitle}</h1>
          <p>{message}</p>
          {state === "done" ? <a className="button button-primary" href={href("/play")}>{copy.common.play}</a> : state === "error" ? <a className="button button-primary" href={href("/login")}>{copy.auth.backLogin}</a> : null}
        </section>
      </main>
    </Page>
  );
}

export function AuthPage({ mode, session, onSession }: { mode: AuthMode; session: Session | null; onSession: (session: Session | null) => void }) {
  const { copy, href, isEnglish } = useLocale();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  if (mode === "verify-email") return <VerifyEmail session={session} />;

  const content = {
    login: { title: copy.auth.loginTitle, lead: copy.auth.loginLead, submit: copy.auth.loginSubmit },
    register: { title: copy.auth.registerTitle, lead: copy.auth.registerLead, submit: copy.auth.registerSubmit },
    "forgot-password": { title: copy.auth.forgotTitle, lead: copy.auth.forgotLead, submit: copy.auth.forgotSubmit },
    "reset-password": { title: copy.auth.resetTitle, lead: copy.auth.resetLead, submit: copy.auth.resetSubmit },
  }[mode];

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError("");
    setNotice("");
    const data = Object.fromEntries(new FormData(event.currentTarget).entries());
    const token = new URLSearchParams(window.location.search).get("token") || "";
    const returnTo = new URLSearchParams(window.location.search).get("return_to") || "/play";
    try {
      const result = await api<{ user?: Session; needsEmailVerification?: boolean; sent?: boolean; reset?: boolean; returnTo?: string }>(
        `/api/auth/${mode}`,
        { method: "POST", body: JSON.stringify({ ...data, token, returnTo }) },
      );
      if (mode === "login" && result.user) {
        onSession(result.user);
        await identifyAnalytics();
        window.location.assign(href(result.returnTo || "/play"));
      } else if (mode === "register") {
        setNotice(copy.auth.verificationSent);
      } else if (mode === "forgot-password") {
        setNotice(copy.auth.resetSent);
      } else if (mode === "reset-password") {
        setNotice(copy.auth.resetDone);
      }
    } catch (caught) {
      setError(!isEnglish && caught instanceof Error ? caught.message : copy.auth.genericError);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Page session={session} className="auth-page">
      <main id="main" className="auth-shell page-shell">
        <section className="auth-card">
          <p className="eyebrow">{copy.auth.account}</p>
          <h1>{content.title}</h1>
          <p className="auth-lead">{content.lead}</p>
          {notice ? <div className="form-message success" role="status">{notice}</div> : null}
          {error ? <div className="form-message error" role="alert">{error}</div> : null}
          {!notice || mode === "login" ? (
            <form onSubmit={(event) => void submit(event)}>
              {mode === "register" ? <label className="form-field"><span>{copy.auth.name}</span><input name="name" type="text" autoComplete="name" maxLength={80} /></label> : null}
              {mode !== "reset-password" ? <label className="form-field"><span>{copy.auth.email}</span><input name="email" type="email" required autoComplete="email" /></label> : null}
              {mode === "login" || mode === "register" || mode === "reset-password" ? (
                <PasswordField label={mode === "reset-password" ? copy.auth.newPassword : copy.auth.password} autoComplete={mode === "login" ? "current-password" : "new-password"} />
              ) : null}
              <button className="button button-primary form-submit" type="submit" disabled={busy}>
                {busy ? <LoaderCircle className="spinner" aria-hidden="true" /> : null}{busy ? copy.auth.working : content.submit}
              </button>
            </form>
          ) : null}
          <div className="auth-links">
            {mode === "login" ? <><a href={href("/forgot-password")}>{copy.auth.forgot}</a><span>{copy.auth.noAccount}<a href={href("/register")}>{copy.auth.create}</a></span></> : null}
            {mode === "register" ? <><span>{copy.auth.agreementA}<a href={href("/legal/terms")}>{copy.common.terms}</a>{copy.auth.agreementB}<a href={href("/legal/privacy")}>{copy.common.privacy}</a>.</span><a href={href("/login")}>{copy.auth.hasAccount}</a></> : null}
            {mode === "forgot-password" || mode === "reset-password" ? <a href={href("/login")}>{copy.auth.backLogin}</a> : null}
          </div>
        </section>
      </main>
    </Page>
  );
}
